import { useCallback, useContext } from 'react';
import { FixtureActionCommentSocket } from '@/service/types';
import {
  SCORING_WORKER_HOST_ACTION,
  ActionCommentAddMsg,
  ActionCommentUpdateMsg,
  ActionCommentRemoveMsg,
} from '@/workers/scoring/types';
import { ScoringContext } from '@/contexts/scoring/context';
import { API_METHOD } from '@/service/constants';
import { SUCCESS_STRING } from '@/constants/dictionary';

export type CommentActionType = {
  commentActionType: API_METHOD.POST | API_METHOD.PUT | API_METHOD.DELETE;
  comment: FixtureActionCommentSocket;
};

export const useCommentAction = () => {
  const { useDispatchWithResponse } = useContext(ScoringContext);

  const addCommentAction = useDispatchWithResponse<ActionCommentAddMsg>(
    SCORING_WORKER_HOST_ACTION.ACTION_COMMENT_ADD,
  );
  const updateCommentAction = useDispatchWithResponse<ActionCommentUpdateMsg>(
    SCORING_WORKER_HOST_ACTION.ACTION_COMMENT_UPDATE,
  );

  const removeCommentAction = useDispatchWithResponse<ActionCommentRemoveMsg>(
    SCORING_WORKER_HOST_ACTION.ACTION_COMMENT_REMOVE,
  );

  const commentAction = useCallback(
    ({ commentActionType, comment }: CommentActionType) => {
      if (commentActionType === API_METHOD.POST) {
        addCommentAction.dispatch(
          {
            collectionId: comment.collectionId,
            fixtureId: comment.fixtureId,
            fixtureActionId: comment.fixtureActionId || '',
            comment: comment.comment || '',
          },
          { successMessage: SUCCESS_STRING.COMMENT_SUCCESSFULLY_ADDED },
        );
      }

      if (commentActionType === API_METHOD.PUT) {
        if (comment.fixtureActionId) {
          updateCommentAction.dispatch(
            {
              collectionId: comment.collectionId,
              fixtureId: comment.fixtureId,
              fixtureActionId: comment.fixtureActionId,
              comment: comment.comment || '',
            },
            {
              successMessage: SUCCESS_STRING.COMMENT_SUCCESSFULLY_UPDATED,
            },
          );
        }
      }

      if (commentActionType === API_METHOD.DELETE) {
        if (comment.fixtureActionId) {
          removeCommentAction.dispatch(
            {
              collectionId: comment.collectionId,
              fixtureId: comment.fixtureId,
              fixtureActionId: comment.fixtureActionId,
            },
            {
              successMessage: SUCCESS_STRING.COMMENT_SUCCESSFULLY_REMOVED,
            },
          );
        }
      }
    },
    [addCommentAction, updateCommentAction, removeCommentAction],
  );

  return {
    commentAction,
  };
};
